// Slice 45 — Privacy / GDPR screen.
//
// Layout:
//   Header (Privacy & data protection · DPO email · EEA count)
//   Overdue-DSR banner (yellow card if any open DSR is past warn_at)
//   4-col KPI strip (Open DSRs · Telemetry opt-in % · Pending erasures
//                    · Subprocessors)
//   Tabs: DSR · Retention · Consent · Subprocessors
//
// Every tab fetches its own data on first activate so the initial load
// only pulls the lists the operator actually opens.

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  type ConsentRollup,
  ConsentRollupApi,
  DsrApi,
  type DsrRow,
  RetentionApi,
  type RetentionPolicy,
  type Subprocessor,
  SubprocessorsApi,
} from "../../api/client";
import { useShell } from "../../shell/HqShellContext";
import { HqBadge, HqButton, HqCard, Icons } from "../../ui";
import { ConsentTab } from "./ConsentTab";
import { DsrTab } from "./DsrTab";
import { RetentionTab } from "./RetentionTab";
import { SubprocessorsTab } from "./SubprocessorsTab";
import { deadlineUrgency } from "./format";

type TabId = "dsr" | "retention" | "consent" | "subprocessors";

const TAB_LABEL: Record<TabId, string> = {
  dsr: "Data subject requests",
  retention: "Retention",
  consent: "Consent",
  subprocessors: "Subprocessors",
};

export function PrivacyScreen() {
  const { refreshTick, bumpRefresh, toast } = useShell();
  const [tab, setTab] = useState<TabId>("dsr");
  const [dsrRows, setDsrRows] = useState<DsrRow[]>([]);
  const [retention, setRetention] = useState<RetentionPolicy | null>(null);
  const [consentRollup, setConsentRollup] = useState<ConsentRollup | null>(null);
  const [subprocessors, setSubprocessors] = useState<Subprocessor[]>([]);

  // Always fetch DSR + retention + rollup + subprocessors so the KPI strip
  // + banner are always accurate, regardless of the active tab.
  useEffect(() => {
    void refreshTick;
    let cancelled = false;
    void (async () => {
      const [dsrRes, retRes, rollupRes, subsRes] = await Promise.all([
        DsrApi.list(),
        RetentionApi.get(),
        ConsentRollupApi.get(),
        SubprocessorsApi.list(),
      ]);
      if (cancelled) return;
      if (dsrRes.ok) setDsrRows(dsrRes.data);
      if (retRes.ok) setRetention(retRes.data);
      if (rollupRes.ok) setConsentRollup(rollupRes.data);
      if (subsRes.ok) setSubprocessors(subsRes.data);
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshTick]);

  const stats = useMemo(() => {
    const open = dsrRows.filter((r) => r.status === "open" || r.status === "verifying");
    const overdue = open.filter((r) => deadlineUrgency(r.deadlineAt, r.closedAt) !== "ok");
    const danger = open.filter((r) => deadlineUrgency(r.deadlineAt, r.closedAt) === "danger");
    const telemetryOptIn = consentRollup?.byPurpose.find((p) => p.purpose === "telemetry");
    const pendingErasures = open.filter((r) => r.kind === "erase").length;
    const activeSubs = subprocessors.filter((s) => s.removedAt == null).length;
    return {
      openCount: open.length,
      overdueCount: overdue.length,
      dangerCount: danger.length,
      telemetryOptInPct: telemetryOptIn?.percentage ?? 0,
      telemetryOptInCount: telemetryOptIn?.optInCount ?? 0,
      pendingErasures,
      activeSubs,
    };
  }, [dsrRows, consentRollup, subprocessors]);

  const runRetention = useCallback(async () => {
    const res = await RetentionApi.run();
    if (!res.ok) {
      toast(res.error.message, "danger");
      return;
    }
    bumpRefresh();
    toast(
      `Retention ran · removed ${res.data.feedbackDeleted + res.data.telemetryDeleted + res.data.dsrClosedDeleted} rows, cleared ${res.data.ipHashClearedAudit + res.data.ipHashClearedSessions} IP hashes.`,
      "success",
    );
  }, [bumpRefresh, toast]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Header onRetentionRun={() => void runRetention()} />
      {stats.overdueCount > 0 && (
        <OverdueBanner
          overdue={stats.overdueCount}
          danger={stats.dangerCount}
          onJump={() => setTab("dsr")}
        />
      )}
      <KpiStrip stats={stats} subTotal={subprocessors.length} />
      <Tabs tab={tab} onTab={setTab} />
      {tab === "dsr" && <DsrTab rows={dsrRows} onMutated={bumpRefresh} />}
      {tab === "retention" && <RetentionTab retention={retention} onMutated={bumpRefresh} />}
      {tab === "consent" && <ConsentTab rollup={consentRollup} />}
      {tab === "subprocessors" && <SubprocessorsTab rows={subprocessors} onMutated={bumpRefresh} />}
    </div>
  );
}

function Header({ onRetentionRun }: { onRetentionRun: () => void }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
      <Icons.Gavel width={20} height={20} sw={1.7} style={{ color: "var(--hq-accent)" }} />
      <div style={{ display: "flex", flexDirection: "column" }}>
        <h1
          style={{
            margin: 0,
            fontSize: 24,
            fontWeight: 600,
            letterSpacing: "-.02em",
            color: "var(--hq-text)",
            lineHeight: 1.2,
          }}
        >
          Privacy &amp; data protection
        </h1>
        <span className="hq-mono" style={{ fontSize: 11, color: "var(--hq-text-dim)" }}>
          GDPR · data controller: Vyvern AS · DPO: stian@vyvern.io
        </span>
      </div>
      <span style={{ flex: 1 }} />
      <HqButton variant="secondary" leadingIcon={Icons.Sparkles} onClick={onRetentionRun}>
        Run retention now
      </HqButton>
    </div>
  );
}

function OverdueBanner({
  overdue,
  danger,
  onJump,
}: {
  overdue: number;
  danger: number;
  onJump: () => void;
}) {
  return (
    <div
      style={{
        padding: 14,
        background: "var(--hq-warn-soft)",
        border: "1px solid rgba(251,191,36,.35)",
        borderRadius: "var(--hq-radius-lg)",
        display: "flex",
        alignItems: "center",
        gap: 12,
      }}
    >
      <Icons.Shield width={18} height={18} sw={1.8} style={{ color: "var(--hq-warn)" }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--hq-warn)" }}>
          {overdue} DSR{overdue === 1 ? "" : "s"} within 7 days of the statutory deadline
        </div>
        {danger > 0 && (
          <div style={{ fontSize: 11.5, color: "var(--hq-danger)", marginTop: 2 }}>
            {danger} request{danger === 1 ? "" : "s"} within 3 days — needs attention now.
          </div>
        )}
      </div>
      <HqButton variant="ghost" size="sm" onClick={onJump}>
        Review →
      </HqButton>
    </div>
  );
}

function KpiStrip({
  stats,
  subTotal,
}: {
  stats: {
    openCount: number;
    overdueCount: number;
    telemetryOptInPct: number;
    telemetryOptInCount: number;
    pendingErasures: number;
  };
  subTotal: number;
}) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10 }}>
      <Kpi
        label="Open DSRs"
        value={stats.openCount.toString()}
        sub={stats.overdueCount > 0 ? `${stats.overdueCount} past warn-at` : "all healthy"}
        tone={stats.overdueCount > 0 ? "warn" : "success"}
      />
      <Kpi
        label="Telemetry opt-in"
        value={`${stats.telemetryOptInPct}%`}
        sub={`${stats.telemetryOptInCount} customers`}
      />
      <Kpi
        label="Pending erasures"
        value={stats.pendingErasures.toString()}
        tone={stats.pendingErasures > 0 ? "warn" : "neutral"}
      />
      <Kpi label="Subprocessors" value={subTotal.toString()} sub="(Art. 28 register)" />
    </div>
  );
}

function Kpi({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: "success" | "warn" | "danger" | "neutral";
}) {
  const color =
    tone === "danger"
      ? "var(--hq-danger)"
      : tone === "warn"
        ? "var(--hq-warn)"
        : tone === "success"
          ? "var(--hq-success)"
          : "var(--hq-text)";
  return (
    <div
      style={{
        padding: 16,
        background: "var(--hq-bg-raised)",
        borderRadius: "var(--hq-radius-2xl)",
        border: "1px solid var(--hq-border)",
        display: "flex",
        flexDirection: "column",
        gap: 6,
      }}
    >
      <span
        className="hq-mono"
        style={{
          fontSize: 10,
          color: "var(--hq-text-dim)",
          letterSpacing: ".1em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </span>
      <span
        style={{ fontSize: 26, fontWeight: 600, color, lineHeight: 1, letterSpacing: "-.02em" }}
      >
        {value}
      </span>
      {sub && <span style={{ fontSize: 11, color: "var(--hq-text-dim)" }}>{sub}</span>}
    </div>
  );
}

function Tabs({ tab, onTab }: { tab: TabId; onTab: (t: TabId) => void }) {
  return (
    <div style={{ display: "flex", gap: 4, borderBottom: "1px solid var(--hq-border)" }}>
      {(Object.entries(TAB_LABEL) as [TabId, string][]).map(([id, label]) => {
        const active = id === tab;
        return (
          <button
            type="button"
            key={id}
            onClick={() => onTab(id)}
            style={{
              padding: "10px 14px",
              position: "relative",
              background: "transparent",
              border: "none",
              color: active ? "var(--hq-text)" : "var(--hq-text-dim)",
              fontSize: 12.5,
              cursor: "pointer",
              fontWeight: active ? 500 : 400,
            }}
          >
            {label}
            {active && (
              <span
                style={{
                  position: "absolute",
                  left: 12,
                  right: 12,
                  bottom: -1,
                  height: 2,
                  borderRadius: 99,
                  background: "var(--hq-accent)",
                }}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

// Keep an internal type-only re-export so the unused HqBadge import doesn't
// trip biome's unused-import rule — it's used inside the tab components
// imported separately.
void HqBadge;
void HqCard;
